import React, { useEffect, useMemo, useRef, useState } from "react";
import { useStripeAppearance } from "@/lib/stripeAppearance";
import { Elements, PaymentElement, AddressElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { X, Trash2, Loader2, Lock, ShoppingBag, CheckCircle2 } from "lucide-react";
import { getStripe, fmtPrice } from "@/lib/stripe";
import { CoinPrice } from "@/components/common/CoinPrice";
import { platformClient } from "@/lib/musicApi";
import { usePlatformAuth } from "@/contexts/PlatformAuthContext";
import { useShopCart, cartGaItems } from "@/contexts/ShopCartContext";
import { gaEcommerce, trackPurchase } from "@/lib/track";
import { CheckoutDetails } from "@/components/shop/CheckoutDetails";
import { setPlatformToken } from "@/lib/musicApi";
import { PasswordInput } from "@/components/PasswordInput";
import i18n from "@/i18n";
import { Trans, useTranslation } from "react-i18next";
import { shopTypeLabel } from "@/lib/shopType";
import { FreeShippingBadge } from "@/components/shop/FreeShippingBadge";
import { TileImage } from "@/components/common/Skeleton";
import { BuyCoinsPanel } from "@/components/wallet/BuyCoinsPanel";
import { coinsExact } from "@/lib/coins";
import { coinVars, balanceVars } from "@/lib/coins";
import { isRateLimited, RATE_LIMITED_KEY } from "@/lib/apiErrors";
import { restrictedText } from "@/lib/restricted";
import { CoinCartQuote } from "@/components/shop/CoinCartQuote";
import { useNavigate } from "react-router-dom";
import { streamNowPath } from "@/lib/streamNow";

const field = { background: "var(--bg-subtle)", border: "1px solid var(--border-color)", color: "var(--text-primary)" };
const line = { borderColor: "var(--border-color)" };

const toApiItem = (i) => (i.kind === "digital"
  ? { kind: "digital", content_type: i.content_type, content_id: i.content_id }
  : { kind: "physical", product_id: i.product_id, printful_variant_id: i.printful_variant_id, color: i.color, size: i.size, qty: i.qty || 1 });

const CartRow = ({ i, onRemove }) => {
  const { t } = useTranslation();
  return (
  <div className="flex items-center gap-3 p-2 rounded-xl" style={{ background: "var(--bg-subtle)" }} data-testid={`shop-cart-row-${i.key}`}>
    {i.cover ? <span className="relative flex-shrink-0 block" style={{ width: 44, height: 44, borderRadius: 6, overflow: "hidden" }}><TileImage src={i.cover} className="w-full h-full object-cover" /></span> : <div style={{ width: 44, height: 44, borderRadius: 6, background: "var(--bg-surface)" }} />}
    <div className="min-w-0 flex-1">
      <p className="text-sm font-medium truncate" style={{ color: "var(--text-primary)" }}>{i.title}</p>
      <p className="text-xs" style={{ color: "var(--text-secondary)" }}>
        {i.type_code ? shopTypeLabel(i.type_code, t) : (i.type_label || (i.kind === "physical" ? t("shop.merch") : ""))}
        {i.kind === "physical" && (i.color || i.size) ? ` · ${[i.color, i.size].filter(Boolean).join(" / ")}` : ""}
        {i.qty > 1 ? ` · x${i.qty}` : ""}
      </p>
    </div>
    {i.kind === "physical"
      ? <span className="text-sm font-semibold whitespace-nowrap" style={{ color: "var(--text-primary)" }}>{fmtPrice((i.unit_price_cents * (i.qty || 1)) / 100)}</span>
      : <CoinPrice cents={i.unit_price_cents * (i.qty || 1)} size="sm" />}
    {onRemove && <button onClick={() => onRemove(i.key)} data-testid={`shop-cart-remove-${i.key}`} style={{ color: "#ef4444" }}><Trash2 size={15} /></button>}
  </div>
  );
};

const CardForm = ({ paymentIntentId, draftId, total, subtotal, surcharge, taxCents, taxPending, savable, items, onDone }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [save, setSave] = useState(true);
  const [tax, setTax] = useState(taxCents || 0);
  const [grandTotal, setGrandTotal] = useState(total || 0);
  const [taxResolved, setTaxResolved] = useState(!taxPending);
  const [calcBusy, setCalcBusy] = useState(false);
  const onAddressChange = async (e) => {
    if (!taxPending) return;
    // Any edit invalidates the shown tax until the new address resolves.
    setTaxResolved(false);
    if (!e.complete) return;
    setCalcBusy(true); setError("");
    try {
      const { data } = await platformClient.post("/shop/checkout/cart/tax", {
        draft_id: draftId, payment_intent_id: paymentIntentId, address: e.value.address });
      setTax(data.tax_cents || 0); setGrandTotal(data.total_cents || grandTotal); setTaxResolved(true);
    } catch { setError(t("shop.errTax")); }
    setCalcBusy(false);
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true); setError("");
    if (savable && !save) {
      try { await platformClient.post("/payments/save-preference", { payment_intent_id: paymentIntentId, save: false }); } catch { /* non-blocking */ }
    }
    const { error: err, paymentIntent } = await stripe.confirmPayment({ elements, redirect: "if_required" });
    if (err) { setError(err.message || t("shop.payFailed")); setBusy(false); return; }
    if (paymentIntent && (paymentIntent.status === "succeeded" || paymentIntent.status === "processing")) {
      trackPurchase({ eventId: paymentIntent.id, category: "shop", purchaseType: "cart", items: cartGaItems(items || []),
        valueCents: paymentIntent.amount, currency: (paymentIntent.currency || "usd").toUpperCase() });
      let confirmData = {};
      try { const { data } = await platformClient.post("/shop/checkout/cart/confirm", { payment_intent_id: paymentIntentId }); confirmData = data || {}; } catch { /* webhook backup */ }
      onDone(confirmData);
      return;
    }
    setError(t("shop.payIncomplete")); setBusy(false);
  };
  const showBreakdown = surcharge > 0 || tax > 0 || taxPending;
  return (
    <form onSubmit={submit} data-testid="shop-cart-card-form">
      {taxPending && (
        <div className="mb-3" data-testid="shop-cart-address">
          <label className="text-xs mb-1 block" style={{ color: "var(--text-secondary)" }}>{t("shop.billingAddress")}</label>
          <AddressElement options={{ mode: "billing" }} onChange={onAddressChange} />
        </div>
      )}
      {showBreakdown && (
        <div className="mb-3 rounded-xl p-3 text-sm" data-testid="shop-cart-shipping-breakdown" style={{ background: "var(--bg-subtle)", border: "1px solid var(--border-color)" }}>
          <div className="flex justify-between py-0.5" style={{ color: "var(--text-secondary)" }}>
            <span>{t("shipping.subtotal")}</span><span>{fmtPrice(subtotal / 100)}</span>
          </div>
          {surcharge > 0 && (
            <div className="flex justify-between py-0.5" style={{ color: "var(--text-secondary)" }} data-testid="shop-cart-intl-shipping">
              <span>{t("shipping.intlLine")}</span><span>{fmtPrice(surcharge / 100)}</span>
            </div>
          )}
          <div className="flex justify-between py-0.5" style={{ color: "var(--text-secondary)" }} data-testid="shop-cart-tax">
            <span>{t("shipping.tax")}</span>
            <span>{taxPending && !taxResolved ? (calcBusy ? "..." : "-") : fmtPrice(tax / 100)}</span>
          </div>
          <div className="flex justify-between py-0.5 mt-1 pt-1 font-semibold" style={{ color: "var(--text-primary)", borderTop: "1px solid var(--border-color)" }}>
            <span>{t("shipping.total")}</span><span>{fmtPrice(grandTotal / 100)}</span>
          </div>
        </div>
      )}
      <PaymentElement options={{ layout: "tabs" }} />
      {savable && (
        <label className="flex items-center gap-2 mt-3 cursor-pointer select-none" data-testid="shop-save-card-label">
          <input type="checkbox" checked={save} onChange={(e) => setSave(e.target.checked)} data-testid="shop-save-card" />
          <span className="text-sm" style={{ color: "var(--text-secondary)" }}>{t("billing.saveCardForReuse")}</span>
        </label>
      )}
      <p className="my-3 text-xs" style={{ color: "var(--text-secondary)" }}>
        <Trans i18nKey="shop.checkoutConsent" components={{ t: <a href="/terms" className="underline" />, r: <a href="/refund" className="underline" /> }} />
      </p>
      {error && <p className="text-sm mb-3" style={{ color: "#ef4444" }} data-testid="shop-cart-pay-error">{error}</p>}
      <button type="submit" disabled={!stripe || busy || calcBusy || (taxPending && !taxResolved)} data-testid="shop-cart-pay-button" className="w-full py-3 rounded-full text-sm font-semibold flex items-center justify-center gap-2" style={{ background: "var(--accent-primary)", color: "#fff", opacity: (busy || (taxPending && !taxResolved)) ? 0.7 : 1 }}>
        {busy ? <><Loader2 size={16} className="animate-spin" /> {t("shop.processing")}</> : <><Lock size={14} /> {t("shop.pay", { amount: fmtPrice(grandTotal / 100) })}</>}
      </button>
    </form>
  );
};

const PaymentStep = ({ items, shipping, email, consentId, useCoins, coinPlan, onRequote, onFallbackCard, onBuyCoins, onDone, onError }) => {
  const stripeAppearance = useStripeAppearance();
  const { t } = useTranslation();
  const [clientSecret, setClientSecret] = useState("");
  const [customerSessionSecret, setCustomerSessionSecret] = useState("");
  const [hasSavedCard, setHasSavedCard] = useState(false);
  const [piId, setPiId] = useState("");
  const [draftId, setDraftId] = useState("");
  const [total, setTotal] = useState(0);
  const [subtotal, setSubtotal] = useState(0);
  const [surcharge, setSurcharge] = useState(0);
  const [taxCents, setTaxCents] = useState(0);
  const [taxPending, setTaxPending] = useState(false);
  const [coinsCents, setCoinsCents] = useState(0);
  const [coinError, setCoinError] = useState(null);
  const [error, setError] = useState("");
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    platformClient.post("/shop/checkout/cart", { items: items.map(toApiItem), shipping_address: shipping, email: email || undefined, lang: (i18n?.language || "en").split("-")[0], consent_id: consentId || undefined, use_coins: !!useCoins,
      billing_address: coinPlan?.billing || undefined, expected_coin_cents: coinPlan ? coinPlan.expected : undefined }, { restrictedInline: true })
      .then(({ data }) => {
        // Coin-only digital order: no Stripe object was created, access is already granted.
        if (data.paid_with === "coins") { onDone({ paid_with: "coins" }); return; }
        // Coins could not cover the digital side (rare race) or wallet frozen: surface it
        // and let the buyer continue by card. Never a silent fallback.
        // Tax or total moved since the quote: nothing was spent, re-quote with the reason shown.
        if (["amount_changed", "tax_unavailable"].includes(data.coin_error)) { onRequote?.(data.coin_error === "tax_unavailable" ? "coins.taxUnavailable" : "coins.amountChanged"); return; }
        if (data.coin_error) { setCoinError(data); return; }
        setClientSecret(data.client_secret); setPiId(data.payment_intent_id); setDraftId(data.draft_id); setTotal(data.amount_cents);
        gaEcommerce("begin_checkout", { items: cartGaItems(items), valueCents: data.amount_cents, checkout_type: "shop" });
        setCustomerSessionSecret(data.customer_session_client_secret || "");
        setHasSavedCard(!!data.has_saved_card);
        setSubtotal(data.subtotal_cents ?? data.amount_cents); setSurcharge(data.shipping_surcharge_cents || 0);
        setTaxCents(data.tax_cents || 0); setTaxPending(!!data.tax_pending);
        setCoinsCents(data.coins_cents || 0);
      })
      .catch((e) => {
        const d = e?.response?.data?.detail;
        if (isRateLimited(e)) { setError(t(RATE_LIMITED_KEY)); return; }
        if (d?.reason === "nothing_to_charge") { onError?.(t("shop.ownEverything")); return; }
        if (d?.reason === "tax_address_invalid") { setError(t("shop.errTax")); return; }
        setError(restrictedText(e, t) || (typeof d === "string" ? d : t("shop.errCheckout")));
      });
  }, [items, shipping, onError, useCoins]);
  if (coinError) {
    return (
      <div data-testid="shop-cart-coin-error">
        <p className="text-sm mb-3" style={{ color: "var(--text-secondary)" }}>
          <Trans i18nKey="cart.coins.needMore" values={{ ...balanceVars(coinsExact(coinError.spendable_cents ?? coinError.balance_cents ?? 0)), needed: t("coins.perPrice", coinVars(Math.ceil((coinError.digital_total_cents || 0) / 20))) }} components={{ a: <button type="button" onClick={onBuyCoins} data-testid="shop-cart-coin-buy" className="underline font-semibold" style={{ color: "var(--accent-primary)", background: "none", border: "none", padding: 0, cursor: "pointer", font: "inherit" }} /> }} />
        </p>
        <button onClick={onFallbackCard} data-testid="shop-cart-coin-fallback" className="w-full py-3 rounded-full text-sm font-semibold" style={{ background: "var(--accent-primary)", color: "#fff" }}>{t("cart.coins.payByCard")}</button>
      </div>
    );
  }
  if (error) return <p className="text-sm" style={{ color: "#ef4444" }} data-testid="shop-cart-checkout-error">{error}</p>;
  if (!clientSecret) return <p className="text-sm flex items-center gap-2" style={{ color: "var(--text-secondary)" }}><Loader2 size={16} className="animate-spin" /> {t("shop.loadingPayment")}</p>;
  return (
    <Elements stripe={getStripe()} options={{ clientSecret, customerSessionClientSecret: customerSessionSecret || undefined, appearance: stripeAppearance }}>
      {coinsCents > 0 && (
        <p className="text-xs mb-2" data-testid="shop-cart-coins-applied" style={{ color: "var(--text-secondary)" }}>{t("cart.coins.coversDigital")}</p>
      )}
      <CardForm items={items} paymentIntentId={piId} draftId={draftId} total={total} subtotal={subtotal} surcharge={surcharge} taxCents={taxCents} taxPending={taxPending} savable={!!customerSessionSecret && !hasSavedCard} onDone={onDone} />
    </Elements>
  );
};

export const SetPasswordPrompt = () => {
  const { t } = useTranslation();
  const [pw, setPw] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const save = async () => {
    if (pw.length < 8) { setErr(t("shop.pwMin")); return; }
    setBusy(true); setErr("");
    try { await platformClient.post("/auth/set-password", { password: pw }); setDone(true); }
    catch (e) { setErr(e?.response?.data?.detail?.reason === "reauth_required" ? t("password.reauthRequired") : t("shop.pwSaveFail")); } finally { setBusy(false); }
  };
  if (done) return <p className="text-xs mb-4" style={{ color: "var(--success)" }} data-testid="shop-setpw-done">{t("shop.pwDone")}</p>;
  return (
    <div className="rounded-xl p-3 mb-4 text-left" data-testid="shop-setpw" style={{ background: "var(--bg-subtle)", border: "1px solid var(--border-color)" }}>
      <p className="text-xs font-medium mb-2" style={{ color: "var(--text-primary)" }}>{t("shop.saveAccountTitle")}</p>
      <p className="text-xs mb-2" style={{ color: "var(--text-secondary)" }}>{t("shop.saveAccountBody")}</p>
      <div className="flex gap-2">
        <PasswordInput value={pw} onChange={(e) => setPw(e.target.value)} placeholder={t("shop.createPassword")} data-testid="shop-setpw-input" wrapperClassName="relative flex-1" className="w-full px-3 py-2 rounded-lg text-sm outline-none" style={field} />
        <button onClick={save} disabled={busy} data-testid="shop-setpw-save" className="px-4 py-2 rounded-full text-xs font-semibold" style={{ background: "var(--accent-primary)", color: "#fff" }}>{busy ? "..." : t("shop.save")}</button>
      </div>
      {err && <p className="text-xs mt-1" style={{ color: "#ef4444" }}>{err}</p>}
    </div>
  );
};

export const ShopCartPanel = () => {
  const { platformUser, refresh } = usePlatformAuth();
  const { t } = useTranslation();
  const { items, removeItem, clear, subtotalCents, hasPhysical, isOpen, closeCart, wantCheckout, consumeWantCheckout } = useShopCart();
  const navigate = useNavigate();
  // Split the drawer subtotal: digital shown in coins, merch stays plain dollars.
  const digitalCents = items.reduce((s, i) => s + (i.kind === "physical" ? 0 : (i.unit_price_cents * (i.qty || 1))), 0);
  const merchCents = subtotalCents - digitalCents;
  const [view, setView] = useState("cart"); // cart | details | pay | done
  const [shipping, setShipping] = useState(null);
  const [guestEmail, setGuestEmail] = useState(null);
  const [consentId, setConsentId] = useState(null);
  const [isShadowBuyer, setIsShadowBuyer] = useState(false);
  const purchasedRef = useRef([]);
  // Coins (Phase 3): only a logged-in buyer with digital items can pay the digital side
  // with wallet coins. digitalCents (coin-clean item prices) is the display baseline; the
  // server enforces the exact digital subtotal plus its tax at spend time.
  const [wallet, setWallet] = useState(null);
  const [useCoins, setUseCoins] = useState(false);
  const [buyOpen, setBuyOpen] = useState(false);
  const [coinsRefreshKey, setCoinsRefreshKey] = useState(0);
  // Coin path: the quote the buyer accepted ({billing, expected}); null = still quoting.
  const [coinPlan, setCoinPlan] = useState(null);
  const [coinNotice, setCoinNotice] = useState("");
  const coinsEligible = !!platformUser && digitalCents > 0;
  const spendableCents = wallet ? (wallet.spendable_cents ?? wallet.balance_cents) : 0;
  const coinsCover = !!(wallet && spendableCents >= digitalCents);
  const loadWallet = React.useCallback(() => {
    if (!coinsEligible) { setWallet(null); return; }
    platformClient.get("/coins/wallet").then(({ data }) => setWallet(data)).catch(() => setWallet(null));
  }, [coinsEligible]);
  useEffect(() => { loadWallet(); }, [loadWallet, isOpen]);
  // After a top-up the buyer never leaves the cart: refresh the wallet so coverage
  // recomputes, and bump the key so the payment step (if mounted) re-quotes.
  const onCoinsCredited = () => { loadWallet(); setCoinsRefreshKey((k) => k + 1); };
  useEffect(() => { setUseCoins(coinsCover); }, [coinsCover]);
  useEffect(() => { setCoinPlan(null); setCoinNotice(""); }, [useCoins, isOpen, coinsRefreshKey]);

  const beginCheckout = React.useCallback(() => {
    // Logged-in with no merch: nothing to collect, straight to payment.
    if (platformUser && !hasPhysical) { setView("pay"); return; }
    setView("details");
  }, [platformUser, hasPhysical]);

  // Resume checkout after Google OAuth return (flag set by /shop).
  useEffect(() => {
    if (isOpen && wantCheckout && platformUser) { consumeWantCheckout(); beginCheckout(); }
  }, [isOpen, wantCheckout, platformUser, consumeWantCheckout, beginCheckout]);

  useEffect(() => { if (!isOpen) { setView("cart"); setShipping(null); setGuestEmail(null); setConsentId(null); setIsShadowBuyer(false); } }, [isOpen]);

  if (!isOpen) return null;

  const onDetailsReady = ({ email, shipping: ship, consentId: cid }) => { setGuestEmail(email); setShipping(ship); setConsentId(cid || null); setView("pay"); };
  const onPaid = async (confirmData) => {
    purchasedRef.current = items;
    // Auto-login a guest into their (shadow) account after a verified payment.
    if (confirmData?.session?.token) {
      setPlatformToken(confirmData.session.token);
      setIsShadowBuyer(!!confirmData.is_shadow_account);
      try { await refresh(); } catch { /* noop */ }
    }
    clear();
    setView("done");
  };
  const digitalBought = purchasedRef.current.some((i) => i.kind === "digital");
  const streamNow = () => { closeCart(); navigate(streamNowPath(purchasedRef.current), { state: { streamNow: Date.now() } }); };

  return (
    <>
    <div className="fixed inset-0 z-[10004] flex justify-end" style={{ background: "rgba(30,27,75,0.45)" }} onClick={closeCart} data-testid="shop-cart-panel">
      <div className="w-full max-w-md h-full overflow-y-auto flex flex-col" style={{ background: "var(--bg-surface)", boxShadow: "var(--elev-3)" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center px-6 py-4 border-b" style={line}>
          <h3 className="text-lg font-bold flex items-center gap-2" style={{ color: "var(--text-primary)" }}>
            <ShoppingBag size={18} /> {view === "done" ? t("shop.hdrDone") : view === "pay" ? t("shop.hdrPay") : view === "details" ? t("shop.hdrDetails") : t("shop.hdrCart")}
          </h3>
          <button onClick={closeCart} data-testid="shop-cart-close" style={{ color: "var(--text-secondary)" }}><X size={20} /></button>
        </div>

        <div className="flex-1 px-6 py-5">
          {view === "done" ? (
            <div className="text-center py-6" data-testid="shop-cart-thankyou">
              <CheckCircle2 size={40} style={{ color: "var(--success)" }} className="mx-auto mb-3" />
              <p className="text-lg font-bold mb-1" style={{ color: "var(--text-primary)" }}>{t("shop.thankYou")}</p>
              <p className="text-sm mb-4" style={{ color: "var(--text-secondary)" }}>
                {digitalBought ? t("shop.thankDigital") + " " : ""}
                {purchasedRef.current.some((i) => i.kind === "physical") ? t("shop.thankMerch") : ""}
              </p>
              <div className="space-y-2 mb-5 text-left">
                {purchasedRef.current.map((i) => <CartRow key={i.key} i={i} />)}
              </div>
              {isShadowBuyer && <SetPasswordPrompt />}
              <div className="flex gap-2">
                {digitalBought && <button type="button" onClick={streamNow} data-testid="shop-cart-stream-now" className="flex-1 py-2.5 rounded-full text-sm font-semibold text-center" style={{ background: "var(--accent-primary)", color: "#fff" }}>{t("shop.streamNow")}</button>}
                <button onClick={closeCart} data-testid="shop-cart-browse-more" className="flex-1 py-2.5 rounded-full text-sm font-semibold" style={{ background: "var(--bg-subtle)", color: "var(--text-primary)", border: "1px solid var(--border-color)" }}>{t("shop.browseMore")}</button>
              </div>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-16" data-testid="shop-cart-empty">
              <ShoppingBag size={32} style={{ color: "var(--text-secondary)" }} className="mx-auto mb-3" />
              <p className="text-sm" style={{ color: "var(--text-secondary)" }}>{t("shop.cartEmpty")}</p>
            </div>
          ) : (
            <>
              <div className="space-y-2 mb-4" data-testid="shop-cart-items">
                {items.map((i) => <CartRow key={i.key} i={i} onRemove={view === "cart" ? removeItem : null} />)}
              </div>
              {digitalCents > 0 && (
                <div className="flex justify-between items-center py-2 border-t" style={line}>
                  <span className="text-sm" style={{ color: "var(--text-secondary)" }}>{t("shop.subtotalDigital")}</span>
                  <span data-testid="shop-cart-subtotal-digital"><CoinPrice cents={digitalCents} size="sm" /></span>
                </div>
              )}
              {merchCents > 0 && (
                <div className={`flex justify-between items-center py-2 ${digitalCents > 0 ? "" : "border-t"}`} style={digitalCents > 0 ? undefined : line}>
                  <span className="text-sm" style={{ color: "var(--text-secondary)" }}>{t("shop.subtotalMerch")}</span>
                  <span className="text-sm font-bold" style={{ color: "var(--text-primary)" }} data-testid="shop-cart-subtotal-merch">{fmtPrice(merchCents / 100)}</span>
                </div>
              )}
              {hasPhysical && (
                <div className="-mt-1 mb-1">
                  <FreeShippingBadge testid="shop-cart-free-shipping-badge" className="mb-0.5" />
                  <p className="text-xs" data-testid="shop-cart-free-shipping-note" style={{ color: "var(--text-secondary)" }}>{t("shipping.freeUsIntlNote")}</p>
                </div>
              )}

              {coinsEligible && wallet && (
                <div className="rounded-xl p-3 mt-2 text-sm" data-testid="shop-cart-coins" style={{ background: "var(--bg-subtle)", border: "1px solid var(--border-color)" }}>
                  {spendableCents >= digitalCents ? (
                    <>
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input type="checkbox" checked={useCoins} onChange={(e) => setUseCoins(e.target.checked)} data-testid="shop-cart-use-coins" />
                        <span style={{ color: "var(--text-primary)" }}>{t("cart.coins.toggleLabel")}</span>
                      </label>
                      <p className="text-sm mt-1" style={{ color: "var(--text-secondary)" }} data-testid="shop-cart-coins-balance">{t("cart.coins.balanceLine", balanceVars(wallet.spendable_coins ?? wallet.balance_coins))}</p>
                      {useCoins && <p className="text-sm mt-1" style={{ color: "var(--text-secondary)" }} data-testid="shop-cart-coins-covers">{merchCents > 0 ? t("cart.coins.coversDigital") : t("cart.coins.coversAll")}</p>}
                    </>
                  ) : (
                    <p className="text-sm" style={{ color: "var(--text-primary)" }} data-testid="shop-cart-coins-needmore">
                      {wallet.balance_cents < 0 ? (
                      <Trans i18nKey="coins.negativeOwing" components={{ a: <button type="button" onClick={() => setBuyOpen(true)} data-testid="shop-cart-coin-buy-cart" className="underline font-semibold" style={{ color: "var(--accent-primary)", background: "none", border: "none", padding: 0, cursor: "pointer", font: "inherit" }} /> }} />
                      ) : (
                      <Trans i18nKey="cart.coins.needMore" values={{ ...balanceVars(wallet.spendable_coins ?? wallet.balance_coins), needed: t("coins.perPrice", coinVars(Math.ceil(digitalCents / 20))) }} components={{ a: <button type="button" onClick={() => setBuyOpen(true)} data-testid="shop-cart-coin-buy-cart" className="underline font-semibold" style={{ color: "var(--accent-primary)", background: "none", border: "none", padding: 0, cursor: "pointer", font: "inherit" }} /> }} />
                      )}
                    </p>
                  )}
                </div>
              )}

              {view === "cart" && (
                <div className="space-y-2 mt-2">
                  <button onClick={beginCheckout} data-testid="shop-cart-checkout" className="w-full py-3 rounded-full text-sm font-semibold flex items-center justify-center gap-2" style={{ background: "var(--accent-primary)", color: "#fff" }}>{t("shop.proceedCheckout")}</button>
                  <button onClick={clear} data-testid="shop-cart-clear" className="w-full py-2 text-xs" style={{ color: "var(--text-secondary)" }}>{t("shop.clearCart")}</button>
                </div>
              )}
              {view === "details" && <div className="mt-2"><CheckoutDetails hasPhysical={hasPhysical} hasDigital={items.some((i) => i.kind === "digital")} onReady={onDetailsReady} /></div>}
              {view === "pay" && useCoins && coinsEligible && !coinPlan && <div className="mt-2"><CoinCartQuote key={`q:${coinNotice}:${coinsRefreshKey}`} items={items} toApiItem={toApiItem} shipping={shipping} hasPhysical={hasPhysical} notice={coinNotice} onAccept={setCoinPlan} onCard={() => setUseCoins(false)} /></div>}
              {view === "pay" && !(useCoins && coinsEligible && !coinPlan) && <div className="mt-2"><PaymentStep key={`${useCoins ? "coins" : "card"}:${coinsRefreshKey}:${coinPlan ? coinPlan.expected : ""}`} items={items} shipping={shipping} email={guestEmail} consentId={consentId} useCoins={useCoins && coinsEligible} coinPlan={coinPlan} onRequote={(k) => { setCoinPlan(null); setCoinNotice(k); }} onFallbackCard={() => setUseCoins(false)} onBuyCoins={() => setBuyOpen(true)} onDone={onPaid} onError={(m) => { alert(m); closeCart(); }} /></div>}
            </>
          )}
        </div>
      </div>
    </div>
      <BuyCoinsPanel open={buyOpen} onOpenChange={setBuyOpen} onCredited={onCoinsCredited} />
    </>
  );
};

export default ShopCartPanel;
